import { action, computed, makeObservable, observable } from 'mobx'

import { Alert } from '@avantstay/backoffice-core'

import { AuthStore } from '@contexts/Auth/AuthStore'

import { NotificationProps } from './OperationsNotification.types'
import * as service from './OperationsNotificationService'
import { asyncComputed } from './utils/asyncComputed'

type AsyncComputedNotifications = ReturnType<typeof asyncComputed<NotificationProps[]>>

export const FIELD_OPERATIONS_ROLE_ID = '1538282b-cea9-11ed-8996-37ee925312e2'
export const ACCOUNT_MANAGEMENT_ROLE_ID = '78995eb5-9ba9-11ef-bb9b-7995a67aed53'

// The unread dot can lag this long while the user navigates; opening the modal always refreshes.
export const NOTIFICATIONS_NAVIGATION_REFRESH_MS = 60_000

export class _OperationsNotificationStore {
  pendingReadNotifications: Set<string> = new Set()
  showNotificationModal: boolean = false

  private _notifications: AsyncComputedNotifications | undefined
  private lastFetchStartedAt = 0

  constructor() {
    makeObservable(this, {
      pendingReadNotifications: observable,
      showNotificationModal: observable,
      notifications: computed,
      hasUnreadNotifications: computed,
      setShowNotificationModal: action.bound,
      addNotificationToReadQueue: action.bound,
      readNotification: action.bound,
      refreshOnNavigation: action.bound,
      processPendingReadNotifications: action.bound,
    })
  }

  get notifications() {
    if (!this._notifications) {
      this._notifications = asyncComputed<NotificationProps[]>(() => {
        this.lastFetchStartedAt = Date.now()
        if (
          AuthStore.hasUserRole(FIELD_OPERATIONS_ROLE_ID) ||
          AuthStore.hasUserRole(ACCOUNT_MANAGEMENT_ROLE_ID)
        ) {
          return service.getNotifications()
        }
        return Promise.resolve([])
      })
    }

    return this._notifications
  }

  get hasUnreadNotifications(): boolean {
    return this.notifications.value?.some(notification => !notification.readAt) ?? false
  }

  refreshOnNavigation() {
    if (Date.now() - this.lastFetchStartedAt < NOTIFICATIONS_NAVIGATION_REFRESH_MS) return
    this.notifications.silentRefresh()
  }

  setShowNotificationModal(value: boolean) {
    this.showNotificationModal = value
    if (!value) {
      this.processPendingReadNotifications()
    }
  }

  addNotificationToReadQueue(notificationId: string) {
    this.pendingReadNotifications.add(notificationId)
  }

  async readNotification(ids: string[], callback?: VoidFunction) {
    return service
      .readNotifications(ids)
      .then(() => {
        callback?.()
      })
      .catch(error => {
        Alert.Error((error as Error).message)
      })
  }

  async processPendingReadNotifications() {
    if (this.pendingReadNotifications.size === 0) return

    const idsToRead = Array.from(this.pendingReadNotifications)

    return this.readNotification(idsToRead, () => {
      this.pendingReadNotifications.clear()
      this.notifications.silentRefresh()
    })
  }
}

export const operationsNotificationStore = new _OperationsNotificationStore()
